import type { ComponentType } from 'react';
import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';

import { AppShell } from '@/components/layout/app-shell';
import { routes } from '@/config/routes';

import { RequireAuth } from './guards/require-auth';

const lazy =
  <K extends string>(loader: () => Promise<Record<K, ComponentType>>, key: K) =>
  async () => {
    const m = await loader();
    return { Component: m[key] };
  };

export const protectedRoutes: RouteObject[] = [
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: routes.home, element: <Navigate to={routes.devices} replace /> },
          {
            path: routes.devices,
            lazy: lazy(() => import('@/pages/devices/devices.page'), 'DevicesPage'),
          },
          {
            path: routes.device(),
            lazy: lazy(
              () => import('@/pages/device-detail/device-detail.page'),
              'DeviceDetailPage',
            ),
          },
          {
            path: routes.deviceSetup(),
            lazy: lazy(() => import('@/pages/device-setup/device-setup.page'), 'DeviceSetupPage'),
          },
          {
            path: routes.map,
            handle: { fullBleed: true },
            lazy: lazy(() => import('@/pages/map/map.page'), 'MapPlaceholderPage'),
          },
          {
            path: routes.subscriptions,
            lazy: lazy(
              () => import('@/pages/subscriptions/subscriptions.page'),
              'SubscriptionsPage',
            ),
          },
          {
            path: routes.subscriptionCheckout,
            lazy: lazy(
              () => import('@/pages/subscriptions/checkout.page'),
              'SubscriptionCheckoutPage',
            ),
          },
          {
            path: routes.subscriptionPayment,
            lazy: lazy(
              () => import('@/pages/subscriptions/payment.page'),
              'SubscriptionPaymentPage',
            ),
          },
          {
            path: routes.subscriptionPaymentApproved,
            lazy: lazy(
              () => import('@/pages/subscriptions/payment-approved.page'),
              'PaymentApprovedPage',
            ),
          },
          {
            path: routes.subscriptionPaymentFailed,
            lazy: lazy(
              () => import('@/pages/subscriptions/payment-failed.page'),
              'PaymentFailedPage',
            ),
          },
          {
            path: routes.warranty,
            lazy: lazy(() => import('@/pages/warranty/warranty-claims.page'), 'WarrantyClaimsPage'),
          },
          {
            path: routes.warrantyClaim(),
            lazy: lazy(() => import('@/pages/warranty/warranty-claims.page'), 'WarrantyClaimsPage'),
          },
          {
            path: routes.notifications,
            lazy: lazy(
              () => import('@/pages/notifications/notification-settings.page'),
              'NotificationSettingsPlaceholderPage',
            ),
          },
          {
            path: routes.profile,
            lazy: lazy(() => import('@/pages/profile/profile.page'), 'ProfilePlaceholderPage'),
          },
        ],
      },
    ],
  },
];
